// SPDX-License-Identifier: LicenseRef-Pachamama-Proprietary
// Copyright 2026 Pachamama Studios B.V. All rights reserved.

/**
 * AYNI — a short line at the foot of the screen with an Undo button
 * (owner, 2026-10-12: the dataset's ✕ closes it and its overlays at
 * once, with no question, and offers this for a few seconds instead).
 *
 *   ┌──────────────────────────────────────────────┐
 *   │ Sea Surface Temperature and its overlays     │
 *   │ closed                              [Undo]   │
 *   └──────────────────────────────────────────────┘
 *
 * One at a time: a new one replaces the last. It goes by itself after
 * `ms` (paused while the pointer or focus is on it, so it can be
 * reached), or with Undo, or when the caller dismisses it. The line is
 * a polite live region, so a screen reader hears what happened and that
 * Undo is there.
 */

import { t } from '../i18n'

export interface UndoToastOptions {
  /** What just happened, in a sentence. */
  message: string
  /** Called once, when Undo is pressed. */
  onUndo(): void
  /** How long it stays (default 6 s). */
  ms?: number
  /** Where to mount it (default: the page body; the phone's AR overlay passes its own). */
  container?: HTMLElement
}

export interface UndoToastHandle {
  readonly element: HTMLElement
  dismiss(): void
}

const DEFAULT_MS = 6000

let current: UndoToastHandle | null = null

/** Take the toast on screen down, if any. */
export function dismissUndoToast(): void {
  current?.dismiss()
}

export function showUndoToast(opts: UndoToastOptions): UndoToastHandle {
  current?.dismiss()
  const el = document.createElement('div')
  el.className = 'undo-toast'
  el.setAttribute('role', 'status')
  el.setAttribute('aria-live', 'polite')
  const text = document.createElement('span')
  text.className = 'undo-toast-text'
  text.textContent = opts.message
  const undo = document.createElement('button')
  undo.type = 'button'
  undo.className = 'undo-toast-undo'
  undo.textContent = t('undo.action')
  el.append(text, undo)

  const ms = opts.ms ?? DEFAULT_MS
  let timer: number | undefined
  let left = ms
  let startedAt = 0
  const start = (): void => {
    startedAt = Date.now()
    timer = window.setTimeout(() => handle.dismiss(), left)
  }
  const pause = (): void => {
    if (timer === undefined) return
    window.clearTimeout(timer)
    timer = undefined
    left = Math.max(1500, left - (Date.now() - startedAt))
  }
  const resume = (): void => {
    if (timer === undefined && !el.matches(':hover') && !el.contains(document.activeElement)) start()
  }
  el.addEventListener('pointerenter', pause)
  el.addEventListener('pointerleave', resume)
  el.addEventListener('focusin', pause)
  el.addEventListener('focusout', () => setTimeout(resume, 0))

  const handle: UndoToastHandle = {
    element: el,
    dismiss() {
      if (current !== handle) return
      current = null
      if (timer !== undefined) window.clearTimeout(timer)
      el.remove()
    },
  }
  undo.addEventListener('click', () => {
    handle.dismiss()
    opts.onUndo()
  })

  current = handle
  ;(opts.container ?? document.body).appendChild(el)
  start()
  return handle
}
